// Microphone availability pre-flight + error mapping.
//
// `getUserMedia` can fail for a dozen reasons and the native error names
// (`NotAllowedError`, `NotFoundError`, `NotReadableError`, `SecurityError`,
// `OverconstrainedError`, `AbortError`) are user-hostile. This module maps
// every plausible failure mode to a friendly title + recovery instruction so
// the user knows EXACTLY what to do, instead of seeing a generic "denied".

export class MicError extends Error {
  constructor(code, title, hint, originalError = null) {
    super(title);
    this.name = "MicError";
    this.code = code; // machine-readable
    this.title = title;
    this.hint = hint;
    this.originalError = originalError;
  }
}

/** Run BEFORE actually trying to record. Returns null if all looks good,
 *  or a MicError describing the blocker. */
export function checkMicAvailability() {
  if (typeof window === "undefined") {
    return new MicError("no-browser", "Microphone unavailable", "Not running in a browser environment.");
  }
  if (!window.isSecureContext) {
    return new MicError(
      "insecure-context",
      "Recording needs HTTPS",
      "The browser only allows microphone access on https:// origins or on localhost.",
    );
  }
  if (!navigator?.mediaDevices?.getUserMedia) {
    return new MicError(
      "unsupported-browser",
      "This browser can't record audio",
      "Try the latest Chrome, Edge, or Firefox — they all support the microphone API.",
    );
  }
  // Iframe blocking — Permissions Policy must allow `microphone` for the
  // child frame, otherwise `getUserMedia` throws SecurityError/NotAllowedError.
  if (window.self !== window.top) {
    // We can detect it BUT not 100% rule it out — only the actual call confirms.
    // We return null here (let the actual call decide) and then map the error
    // afterwards in mapMicError().
  }
  return null;
}

/** Translate a thrown getUserMedia error into a friendly MicError. */
export function mapMicError(error) {
  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  const name = error?.name || "";
  const msg = error?.message || "";

  // Permissions Policy violation inside an iframe — the most common cause
  // for the Emergent preview. Browser-specific quirk: Chrome throws
  // NotAllowedError with message containing "permission" or "policy".
  if (inIframe && (name === "NotAllowedError" || name === "SecurityError")) {
    return new MicError(
      "iframe-blocked",
      "Recording is blocked in the preview frame",
      "Click the 'Open in new tab' icon (top-right of the preview) — Web browsers don't allow microphone access inside embedded iframes by default.",
      error,
    );
  }

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return new MicError(
        "permission-denied",
        "Microphone permission denied",
        "Click the lock/permissions icon in the browser address bar, allow microphone access for this site, then reload.",
        error,
      );
    case "NotFoundError":
    case "DevicesNotFoundError":
      return new MicError(
        "no-device",
        "No microphone detected",
        "Plug in a microphone (or check the input device in your OS sound settings), then click Record again.",
        error,
      );
    case "NotReadableError":
    case "TrackStartError":
      return new MicError(
        "device-busy",
        "Microphone is in use by another app",
        "Close any other app or tab that may be using the mic (Zoom, Discord, Meet, OBS) and try again.",
        error,
      );
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return new MicError(
        "bad-constraints",
        "Microphone settings unsupported",
        "The selected sample rate / channel count isn't supported by this mic. Reload and try a different input device.",
        error,
      );
    case "AbortError":
      return new MicError(
        "aborted",
        "Recording aborted",
        "Something else cancelled the recording before it started. Try again.",
        error,
      );
    case "SecurityError":
      return new MicError(
        "security-blocked",
        "Recording blocked by browser security",
        "Open the app in a new tab via the top-right icon — embedded previews can't access the microphone.",
        error,
      );
    case "TypeError":
      // Browser doesn't support MediaRecorder or getUserMedia signature
      return new MicError(
        "unsupported-browser",
        "Browser doesn't support recording",
        "Use the latest Chrome, Edge, Opera, or Firefox to record vocals.",
        error,
      );
    default:
      return new MicError(
        "unknown",
        msg || "Microphone failed to start",
        "Unexpected error — open the browser console for the full stack trace and try again.",
        error,
      );
  }
}

/** Convenience: returns { ok: true, stream } or { ok: false, micError }. */
export async function probeMic() {
  const pre = checkMicAvailability();
  if (pre) return { ok: false, micError: pre };
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return { ok: true, stream };
  } catch (e) {
    return { ok: false, micError: mapMicError(e) };
  }
}
